import { Lead } from './lead-service';

export class EvolutionService {
  private apiUrl: string = '';
  private apiKey: string = '';
  private instance: string = '';
  private instanceNumber: string = '';

  constructor() {
    /**
     * ATENCAO — o que foi removido daqui, e por que.
     *
     * Este construtor tinha QUATRO fallbacks com valor real:
     *
     *     a URL de producao, a chave em texto puro, o nome da instancia e o
     *     numero de WhatsApp, cada um como valor literal depois de um `||`
     *     sobre a variavel `VITE_EVOLUTION_*` correspondente.
     *
     * Fallback em codigo de navegador nao e valor padrao: e a credencial
     * INTEIRA dentro do JavaScript entregue a qualquer visitante. E ela estava
     * mesmo no bundle compilado — nao era codigo morto.
     *
     * A chave ali era ainda a mesma string usada como senha de admin em
     * `nyroh-sites/App.tsx`. Perder essa instancia de WhatsApp significa perder o
     * historico e as conversas em andamento (docs/NYROH.md): existe UMA.
     *
     * Regra do CLAUDE.md, duas vezes: "Nunca usar fallback de credencial no
     * codigo" e "Nenhuma chave de terceiro em codigo que vai para o navegador".
     *
     * O caminho definitivo e o disparo virar job da fila: o navegador enfileira,
     * o worker chama a Evolution com a chave que vive no servidor. Enquanto isso
     * nao acontece, estas variaveis continuam sendo `VITE_*` — ou seja, ainda
     * publicas para quem abrir o DevTools. A diferenca e que agora sao SUAS,
     * rotacionaveis sem deploy, e a falta delas aparece como erro em vez de
     * funcionar as custas de uma chave exposta.
     */
    const env = (import.meta as any).env ?? {};

    let rawUrl: string = env.VITE_EVOLUTION_API_URL ?? '';
    // Sem `https` o navegador bloqueia por mixed content, e o erro que aparece
    // nao fala de protocolo nenhum.
    if (rawUrl.startsWith('http://')) rawUrl = rawUrl.replace('http://', 'https://');
    if (rawUrl.endsWith('/')) rawUrl = rawUrl.slice(0, -1);

    this.apiUrl = rawUrl;
    this.apiKey = env.VITE_EVOLUTION_API_KEY ?? '';
    this.instance = env.VITE_EVOLUTION_INSTANCE_NAME ?? '';
    this.instanceNumber = env.VITE_EVOLUTION_INSTANCE_NUMBER ?? '';
  }

  /**
   * Mensagem unica para "nao configurado". Os metodos ja checavam isso cada um
   * do seu jeito; agora dizem qual variavel falta, que e o que resolve.
   */
  private exigirConfiguracao(): void {
    const faltando = [
      !this.apiUrl && 'VITE_EVOLUTION_API_URL',
      !this.apiKey && 'VITE_EVOLUTION_API_KEY',
      !this.instance && 'VITE_EVOLUTION_INSTANCE_NAME',
    ].filter(Boolean);
    if (faltando.length > 0) {
      throw new Error(
        `Evolution API nao configurada: falta ${faltando.join(', ')}. ` +
          `Preencha em nyroh-leads/.env.local (local) ou nas variaveis de build do painel.`,
      );
    }
  }

  async fetchWhatsAppProfile(phoneNumber: string): Promise<any> {
    this.exigirConfiguracao();

    // Limpar o número para apenas dígitos
    let cleanNumber = phoneNumber.replace(/\D/g, '');
    
    // Garantir que números brasileiros tenham 55
    if (cleanNumber.length === 10 || cleanNumber.length === 11) {
      if (!cleanNumber.startsWith('55')) {
        cleanNumber = `55${cleanNumber}`;
      }
    }
    
    try {
      console.log(`Buscando perfil para ${cleanNumber} na instância ${this.instance}...`);
      
      const response = await fetch(`${this.apiUrl}/chat/fetchProfile/${this.instance}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': this.apiKey
        },
        body: JSON.stringify({ number: cleanNumber })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData: any;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = errorText;
        }
        
        console.error('Evolution API Detailed Error:', {
          status: response.status,
          instance: this.instance,
          url: `${this.apiUrl}/chat/fetchProfile/${this.instance}`,
          error: errorData
        });

        // Caso o número não exista no WhatsApp, a API pode retornar erros específicos
        const errorMsg = typeof errorData === 'object' ? (errorData.message || '') : String(errorData);
        if (errorMsg.includes('not exists') || errorMsg.includes('not found') || response.status === 404 && errorMsg.includes('number')) {
          return {
            verified: true,
            exists: false,
            lastUpdated: new Date().toISOString()
          };
        }
 
        // Se o erro for 404 e não for sobre o número, provavelmente a instância não existe
        if (response.status === 404) {
          throw new Error(`Instância "${this.instance}" não encontrada na Evolution API.`);
        }
        
        throw new Error(`Erro na Evolution API (${response.status}): ${errorMsg}`);
      }
      
      const data = await response.json();
      
      let pictureBase64 = data.picture;
      if (data.picture) {
        const { convertImageToBase64 } = await import('../utils/image-utils');
        pictureBase64 = await convertImageToBase64(data.picture);
      }

      let category = '';
      let description = data.description || '';
      let businessWebsite = Array.isArray(data.website) ? data.website.join(', ') : (data.website || '');
      
      // Se for conta de negócios, tenta buscar mais informações com fetchBusinessProfile
      if (data.isBusiness) {
        try {
          const bizResponse = await fetch(`${this.apiUrl}/chat/fetchBusinessProfile/${this.instance}`, {
             method: 'POST',
             headers: {
               'Content-Type': 'application/json',
               'apikey': this.apiKey
             },
             body: JSON.stringify({ number: cleanNumber })
          });
          if (bizResponse.ok) {
            const bizData = await bizResponse.json();
            if (bizData.category) category = bizData.category;
            if (bizData.description && !description) description = bizData.description;
            if (bizData.website && !businessWebsite) {
                businessWebsite = Array.isArray(bizData.website) ? bizData.website.join(', ') : bizData.website;
            }
          }
        } catch (bizErr) {
          console.warn('Não foi possível buscar detalhes do business profile:', bizErr);
        }
      }

      return {
        picture: pictureBase64,
        status: data.status?.status || (typeof data.status === 'string' ? data.status : ''),
        pushName: data.name || data.pushName || '',
        isBusiness: !!data.isBusiness,
        description: description,
        businessWebsite: businessWebsite,
        category: category,
        lastUpdated: new Date().toISOString(),
        verified: true,
        exists: true
      };
    } catch (e) {
      console.error('Evolution API Fetch Error:', e);
      throw e;
    }
  }
}

export const evolutionService = new EvolutionService();
